import { useMutation, useQueryClient, type UseMutateFunction } from '@tanstack/react-query';
import { refereeQuestionService } from '../services/referee-question.service';
import type {
  RefereeQuestion,
  CreateRefereeQuestionRequest,
  UpdateRefereeQuestionRequest,
} from '../types/referee-question.types';

interface UpdateParams extends UpdateRefereeQuestionRequest {
  questionId: string;
}

export function useCompanyQuestionMutations(companyId: string): {
  create: UseMutateFunction<RefereeQuestion, Error, CreateRefereeQuestionRequest>;
  update: UseMutateFunction<RefereeQuestion, Error, UpdateParams>;
  remove: UseMutateFunction<void, Error, string>;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (request: CreateRefereeQuestionRequest) =>
      refereeQuestionService.createCompanyQuestion(companyId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-questions', companyId] }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ questionId, ...request }: UpdateParams) =>
      refereeQuestionService.updateCompanyQuestion(companyId, questionId, request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-questions', companyId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (questionId: string) =>
      refereeQuestionService.deleteCompanyQuestion(companyId, questionId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-questions', companyId] }),
  });

  return {
    create: createMutation.mutate,
    update: updateMutation.mutate,
    remove: deleteMutation.mutate,
    isLoading: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    isError: createMutation.isError || updateMutation.isError || deleteMutation.isError,
    isSuccess: createMutation.isSuccess || updateMutation.isSuccess || deleteMutation.isSuccess,
    error: createMutation.error ?? updateMutation.error ?? deleteMutation.error,
  };
}
